import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import { InternationalAddressService } from 'src/app/services/international-address.service';
import { CountryCode } from 'libphonenumber-js';
import moment from 'moment';
import { DateTimeAdapter } from '@danielmoncada/angular-datetime-picker';
import { StudioDateTimeAdapter } from 'src/app/services/studio-date-time.adapter';
import { LocalizationService } from 'src/app/services/localization.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-student-record-entity',
  templateUrl: './student-record-entity.component.html',
  styleUrls: ['./student-record-entity.component.css'],
  // Lets staff type the birthday in the studio's date format.
  providers: [{ provide: DateTimeAdapter, useClass: StudioDateTimeAdapter }]
})
export class StudentRecordEntityComponent extends BaseDialogComponent implements OnInit {

  override eckType = "contacts";
  override bundle = "student_record";
  override entityId = this._injectedDialogData['data']['EntityID'];

  contactList = [];
  field_contacts = [];
  isArchived = false;

  // Address internationalization
  internationalAddressService: InternationalAddressService;
  selectedCountry = 'US'; // For address
  phoneCountry: CountryCode = 'US'; // For phone fields - separate from address
  administrativeAreaLabel = 'State';
  postalCodeLabel = 'ZIP Code';

  // The studio's own country, resolved independently of the editable address.
  studioCountry = '';

  // Placeholder / hint for the typed birthday, in the studio's date format.
  birthdayFormat = 'MM/DD/YYYY';

  // Fiscal Code (Codice Fiscale) is editable when the studio OR the student's
  // address country is Italy/Spain. Reactive to the address country dropdown.
  get showFiscalCode(): boolean {
    return ['IT', 'ES'].includes(this.studioCountry)
      || ['IT', 'ES'].includes(this.selectedCountry);
  }

  /**
   * Handle country change to update labels (for address)
   */
  onCountryChange(countryCode: string) {
    this.selectedCountry = countryCode;
    if (this.internationalAddressService) {
      this.administrativeAreaLabel = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);
      this.postalCodeLabel = this.internationalAddressService.getPostalCodeLabel(countryCode);
    }
  }

  /**
   * Handle phone country change when user manually changes it in the phone field
   */
  onPhoneCountryChange(country: any) {
    if (country && country.iso2) {
      this.phoneCountry = country.iso2.toUpperCase();
      LoggerService.log('Phone country changed to:', this.phoneCountry);
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Resolve the studio's country so the Fiscal Code gate works in edit mode too
    // (where the address country comes from the student, not the studio).
    this._studioConfigService.getCountryCode().subscribe(countryCode => {
      this.studioCountry = countryCode;
      this.birthdayFormat = new LocalizationService().getDateFormatsForCountry(countryCode).calendarDateFormat;
    });

    // Initialize the international address service
    if (!this.internationalAddressService) {
      const injector = (this as any)._injector ||
                       (this as any)._changeDetectorRef?.injector ||
                       (this as any)._el?.nativeElement?.injector;

      if (injector) {
        try {
          this.internationalAddressService = injector.get(InternationalAddressService);
        } catch (e) {
          this.internationalAddressService = new InternationalAddressService();
        }
      } else {
        this.internationalAddressService = new InternationalAddressService();
      }
    }
  }

  override getEntity(eckType: string, bundle: string, EntityID: number, formRef?): void {
    // Block Save for the entire load window (country pre-fetch + deferred parent
    // getEntity), so users can't submit before form values are populated.
    this.formDataLoaded = false;

    // MUST fetch and set country BEFORE calling parent's getEntity
    this._entityRESTService.getEntity(eckType, bundle, EntityID)
      .subscribe((data: any) => {
        LoggerService.log('Loading entity data:', data);
        LoggerService.log('Home phone from DB:', data?.field_home_phone);
        LoggerService.log('Cell phone from DB:', data?.field_cell_phone);

        // Get address country first
        const addressCountry = data?.field_address?.[0]?.country_code || data?.field_address?.country_code;

        // Detect country from E.164 phone number (e.g., +15555555555)
        let phoneCountry = null;
        const phoneToCheck = data?.field_home_phone?.[0]?.value || data?.field_home_phone ||
                            data?.field_cell_phone?.[0]?.value || data?.field_cell_phone;

        LoggerService.log('Phone to check for country:', phoneToCheck);
        LoggerService.log('Address country:', addressCountry);

        if (phoneToCheck && typeof phoneToCheck === 'string') {
          // Try parsing with address country context first
          const parsed = this.phoneNumberService.parseNumber(phoneToCheck, addressCountry as CountryCode);
          LoggerService.log('Parsed phone result:', parsed);

          if (parsed.country) {
            phoneCountry = parsed.country;
          } else if (phoneToCheck.startsWith('+1') || phoneToCheck.startsWith('1')) {
            // Fallback for US/Canada (handles legacy data or invalid numbers)
            phoneCountry = 'US';
          }
        }

        // Set phone country (for phone field display)
        // Priority: 1) Phone number country, 2) Address country, 3) Default to US
        if (phoneCountry) {
          this.phoneCountry = phoneCountry;
        } else if (addressCountry) {
          // No phone number - use address country for phone field
          this.phoneCountry = addressCountry;
        } else {
          // No phone or address - default to US
          this.phoneCountry = 'US';
        }

        LoggerService.log('Final phoneCountry set to:', this.phoneCountry);

        // Set archived status (field_archived comes as string "0" or "1" from API)
        this.isArchived = data?.field_archived === '1' || data?.field_archived === 1;

        // Set address country (for address dropdown)
        if (addressCountry) {
          this.selectedCountry = addressCountry;
          this.onCountryChange(addressCountry);
        } else {
          // Use studio default for address country
          this.loadStudioCountry();
        }

        // Force change detection and delay parent call to ensure bindings update
        this._changeDetectorRef.detectChanges();
        setTimeout(() => {
          super.getEntity(eckType, bundle, EntityID, formRef);
        }, 0);
      });
  }

  /**
   * Rewrite a typed birthday in the full studio format on blur, so what was
   * understood (e.g. 5/12/90 -> 05/12/1990) is visible before saving.
   */
  normalizeBirthday(model: NgModel) {
    if (model.value instanceof Date && !model.invalid) {
      model.control.setValue(model.value);
    }
  }

  /**
   * The owl picker runs on the native adapter, which reads a bare 'YYYY-MM-DD'
   * as UTC midnight (the day before, in US timezones), so it gets a
   * local-midnight Date. Set after the base patch, which swaps any object
   * value for its entity-reference id.
   */
  override setFormValues(formRef = null) {
    super.setFormValues(formRef);

    const birthday = this.entityData?.['field_birthday'];
    this.f?.form?.get('field_birthday')?.setValue(
      typeof birthday === 'string' && birthday ? moment(birthday, 'YYYY-MM-DD').toDate() : null
    );
  }

  override ngAfterContentInit(): void {
    super.ngAfterContentInit();

    // For create mode only (not edit), load studio country
    // Check both actionType and that we don't have an entityId to avoid race conditions
    if (this.actionType === 'create' && !this.entityId) {
      this.loadStudioCountry();
    }
  }

  /**
   * Load the studio's country setting to use as default for student addresses
   */
  loadStudioCountry(): void {
    const configFieldName = 'field_studio_information_ref';
    this._drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          const studioCountry = response?.['field_studio_address']?.[0]?.country_code;
          if (studioCountry) {
            this.selectedCountry = studioCountry;
            this.onCountryChange(studioCountry);
          }
        },
        (error) => {
          LoggerService.error('Error loading studio country:', error);
          // Keep default US if loading fails
        }
      );
  }

  /**
   * Override onSubmit to process phone numbers from ngx-intl-tel-input
   */
  override onSubmit(f: NgForm): void {
    // Guard against saving before async values have populated (e.g. Enter key or
    // a fast click while the entity is still loading), which would wipe data.
    if (!this.formDataLoaded) {
      LoggerService.warn('Save ignored: form data is still loading.');
      return;
    }

    LoggerService.log('=== FORM SUBMIT DEBUG ===');
    LoggerService.log('Raw form values:', f.value);
    LoggerService.log('Home phone before processing:', f.value.field_home_phone);
    LoggerService.log('Cell phone before processing:', f.value.field_cell_phone);
    LoggerService.log('Current phoneCountry:', this.phoneCountry);

    // Process phone numbers before submission using phoneCountry (not address country)
    if (f.value.field_home_phone) {
      const processed = this.filterPhoneNumber(f.value.field_home_phone, this.phoneCountry as CountryCode);
      LoggerService.log('Home phone after filterPhoneNumber:', processed);
      f.value.field_home_phone = processed;
    }
    if (f.value.field_cell_phone) {
      const processed = this.filterPhoneNumber(f.value.field_cell_phone, this.phoneCountry as CountryCode);
      LoggerService.log('Cell phone after filterPhoneNumber:', processed);
      f.value.field_cell_phone = processed;
    }

    // An unreadable typed birthday leaves the model null; saving would wipe
    // the stored date, so stop and point at the field instead.
    const birthdayControl = f.controls['field_birthday'];
    if (birthdayControl?.invalid) {
      birthdayControl.markAsTouched();
      this.errorMessage = 'Check the birthday: enter it as ' + this.birthdayFormat + '.';
      return;
    }

    // The picker yields a Date, which would serialize as a UTC timestamp and
    // shift the day; Drupal's date-only field wants YYYY-MM-DD (null clears it).
    if (f.value.field_birthday) {
      f.value.field_birthday = moment(f.value.field_birthday).format('YYYY-MM-DD');
    } else {
      f.value.field_birthday = null;
    }

    // Remove empty gender field to avoid boolean validation error
    if (f.value.field_gender === '' || f.value.field_gender === null || f.value.field_gender === undefined) {
      delete f.value.field_gender;
    }

    LoggerService.log('Final form values to submit:', f.value);

    // Call parent onSubmit
    super.onSubmit(f);
  }
}
